// Sistema → Sugerencias y errores: lo que mandan los usuarios desde el botón
// 💬 de la barra (ver components/feedback/FeedbackButton). Se leen, se anota
// qué se hizo y se marcan revisados, resueltos o descartados.

import React, { useCallback, useEffect, useRef, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { smallMutedStyle, subtitleStyle, titleStyle } from "../components/ui/pageStyles";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { FeedbackKind, FeedbackStatus, KIND_ICON, KIND_LABEL, ROL_LABEL, STATUS_LABEL } from "../lib/feedback";

export type FeedbackReport = {
  id: number;
  kind: FeedbackKind;
  message: string;
  page: string | null;
  viewport: string | null;
  user_agent: string | null;
  user_id: number | null;
  username: string | null;
  rol: string | null;
  full_name: string | null;
  has_screenshot: boolean;
  status: FeedbackStatus;
  admin_note: string | null;
  updated_by: string | null;
  updated_at: string | null;
  created_at: string;
};

type Filter = FeedbackStatus | "TODOS";
const FILTERS: Filter[] = ["NUEVO", "REVISADO", "RESUELTO", "DESCARTADO", "TODOS"];
const STATUSES: FeedbackStatus[] = ["NUEVO", "REVISADO", "RESUELTO", "DESCARTADO"];
// Cómo se llama la acción que lleva a cada estado.
const STATUS_ACTION: Record<FeedbackStatus, string> = {
  NUEVO: "Volver a nuevo",
  REVISADO: "Marcar revisado",
  RESUELTO: "Marcar resuelto",
  DESCARTADO: "Descartar",
};

/* ===== Estilos (mismo lenguaje que LightdataMonitorPage.tsx) ===== */
const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: "8px 12px", borderRadius: "12px", cursor: "pointer", font: "inherit", fontSize: "13px",
  border: `1px solid ${active ? "var(--primary, #4f8cff)" : "var(--border)"}`, fontWeight: active ? 700 : 400,
  background: active ? "var(--primary-soft, rgba(79,140,255,0.12))" : "transparent", color: "var(--text-primary)",
});
const selectStyle: React.CSSProperties = { background: "transparent", color: "var(--text-primary)", border: "1px solid var(--border)", borderRadius: "10px", padding: "6px 8px", font: "inherit", fontSize: "13px" };
const rowStyle: React.CSSProperties = { padding: "12px 0", borderBottom: "1px solid var(--border)" };
const rowHeadStyle: React.CSSProperties = {
  display: "flex", gap: "12px", alignItems: "flex-start", width: "100%", textAlign: "left", cursor: "pointer",
  background: "transparent", border: "none", color: "var(--text-primary)", font: "inherit", padding: 0,
};
const previewStyle: React.CSSProperties = {
  fontSize: "14px", lineHeight: 1.45, overflow: "hidden", display: "-webkit-box",
  WebkitLineClamp: 2, WebkitBoxOrient: "vertical", whiteSpace: "pre-wrap", wordBreak: "break-word",
};
const noteStyle: React.CSSProperties = {
  width: "100%", minHeight: "70px", boxSizing: "border-box", resize: "vertical", borderRadius: "12px",
  border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)",
  padding: "8px 10px", font: "inherit", fontSize: "13px",
};

export function fecha(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }).format(d);
}

export function quien(r: Pick<FeedbackReport, "full_name" | "username" | "rol">): string {
  const name = r.full_name || r.username || "Usuario borrado";
  return r.rol ? `${name} · ${ROL_LABEL[r.rol] || r.rol}` : name;
}

async function leer(res: Response) {
  return (await res.json().catch(() => ({}))) ?? {};
}

function Screenshot({ id, token }: { id: number; token: string | null }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`/v2/feedback/${id}/screenshot`, { token });
        if (!res.ok) throw new Error();
        objectUrl = URL.createObjectURL(await res.blob());
        if (!cancelled) setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, token]);
  if (failed) return <div style={smallMutedStyle}>No se pudo cargar la captura.</div>;
  if (!url) return <div style={smallMutedStyle}>Cargando captura…</div>;
  return (
    <a href={url} target="_blank" rel="noreferrer" title="Abrir en tamaño real">
      <img src={url} alt={`Captura del reporte ${id}`} style={{ maxWidth: "100%", maxHeight: "360px", borderRadius: "12px", border: "1px solid var(--border)" }} />
    </a>
  );
}

function ReportDetail({ report, token, onSaved }: { report: FeedbackReport; token: string | null; onSaved: (r: Partial<FeedbackReport> & { id: number }) => void }) {
  const [note, setNote] = useState(report.admin_note || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (patch: { status?: FeedbackStatus; admin_note?: string }) => {
    setSaving(true);
    setError("");
    try {
      const res = await authFetch(`/v2/feedback/${report.id}`, { token, json: true, method: "PATCH", body: JSON.stringify(patch) });
      const body = await leer(res);
      if (!res.ok || !body.ok) throw new Error(body.error || "No se pudo guardar");
      onSaved(body.report);
    } catch (e: any) {
      setError(e instanceof TypeError ? "Sin conexión con el servidor." : e?.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const noteChanged = note.trim() !== (report.admin_note || "");

  return (
    <div style={{ marginTop: "12px", paddingLeft: "34px", display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ fontSize: "14px", lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{report.message}</div>
      <div style={{ ...smallMutedStyle, display: "grid", gap: "4px" }}>
        <span>Pantalla: {report.page || "—"}{report.viewport ? ` · ventana ${report.viewport}` : ""}</span>
        <span>Navegador: {report.user_agent || "—"}</span>
        {report.updated_by ? <span>Último cambio: {report.updated_by}, {fecha(report.updated_at)}</span> : null}
      </div>
      {report.has_screenshot ? <Screenshot id={report.id} token={token} /> : null}
      <div>
        <textarea aria-label="Nota interna" style={noteStyle} value={note} placeholder="Nota interna: qué se hizo, ticket, etc." onChange={(e) => setNote(e.target.value)} />
        {noteChanged ? (
          <Button variant="secondary" style={{ minHeight: "36px", padding: "6px 12px", fontSize: "13px", marginTop: "6px" }} disabled={saving} onClick={() => save({ admin_note: note })}>
            Guardar nota
          </Button>
        ) : null}
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {STATUSES.filter((s) => s !== report.status).map((s) => (
          <Button
            key={s}
            variant={s === "DESCARTADO" ? "ghost" : "secondary"}
            style={{ minHeight: "36px", padding: "6px 12px", fontSize: "13px" }}
            disabled={saving}
            onClick={() => save(noteChanged ? { status: s, admin_note: note } : { status: s })}
          >
            {STATUS_ACTION[s]}
          </Button>
        ))}
      </div>
      {error ? <div role="alert" style={{ fontSize: "13px", color: "var(--danger, #d1242f)" }}>{error}</div> : null}
    </div>
  );
}

export default function FeedbackPage() {
  const { token } = useAuth();
  const [filter, setFilter] = useState<Filter>("NUEVO");
  const [kind, setKind] = useState<"" | FeedbackKind>("");
  const [reports, setReports] = useState<FeedbackReport[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);

  // Solo vale la respuesta del último pedido: al pasar rápido de una pestaña
  // a otra, la de la anterior podía llegar después y dejar su lista.
  const ultimoPedido = useRef(0);
  const load = useCallback(async () => {
    const pedido = ++ultimoPedido.current;
    const vigente = () => pedido === ultimoPedido.current;
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams({ status: filter });
      if (kind) qs.set("kind", kind);
      const res = await authFetch(`/v2/feedback?${qs}`, { token });
      const body = await leer(res);
      if (!vigente()) return;
      if (!res.ok || !body.ok) throw new Error(body.error || "No se pudieron cargar los reportes");
      setReports(body.reports || []);
      setCounts(body.counts || {});
    } catch (e: any) {
      if (vigente()) setError(e instanceof TypeError ? "Sin conexión con el servidor." : e?.message || "Error desconocido");
    } finally {
      if (vigente()) setLoading(false);
    }
  }, [token, filter, kind]);

  useEffect(() => { load(); }, [load]);

  // Al cambiar de estado, el reporte se va de la lista filtrada (salvo en
  // «Todos»); el contador se corrige sin volver a pedir todo.
  const onSaved = (patch: Partial<FeedbackReport> & { id: number }) => {
    const prev = reports.find((r) => r.id === patch.id);
    if (prev && patch.status && patch.status !== prev.status) {
      setCounts((c) => ({ ...c, [prev.status]: Math.max(0, (c[prev.status] || 0) - 1), [patch.status as string]: (c[patch.status as string] || 0) + 1 }));
      if (filter !== "TODOS") {
        setReports((rs) => rs.filter((r) => r.id !== patch.id));
        setOpenId(null);
        return;
      }
    }
    setReports((rs) => rs.map((r) => (r.id === patch.id ? { ...r, ...patch } : r)));
  };

  const total = STATUSES.reduce((n, s) => n + (counts[s] || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <h2 style={titleStyle}>Sugerencias y errores</h2>
        <p style={subtitleStyle}>
          Lo que mandan admins, tiendas y choferes desde el botón 💬 de la barra superior. Cada reporte trae quién lo mandó,
          desde qué pantalla y, si la adjuntaron, una captura.
        </p>
      </Card>

      <Card>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginBottom: "8px" }}>
          {FILTERS.map((f) => (
            <button key={f} type="button" style={tabStyle(filter === f)} onClick={() => { setFilter(f); setOpenId(null); }}>
              {f === "TODOS" ? `Todos (${total})` : `${STATUS_LABEL[f]} (${counts[f] || 0})`}
            </button>
          ))}
          <select aria-label="Tipo" style={{ ...selectStyle, marginLeft: "auto" }} value={kind} onChange={(e) => setKind(e.target.value as "" | FeedbackKind)}>
            <option value="">Errores y sugerencias</option>
            <option value="ERROR">Solo errores</option>
            <option value="SUGERENCIA">Solo sugerencias</option>
          </select>
        </div>

        {error ? <div role="alert" style={{ fontSize: "14px", color: "var(--danger, #d1242f)", padding: "12px 0" }}>{error}</div> : null}
        {loading && !reports.length ? <div style={{ ...smallMutedStyle, padding: "12px 0" }}>Cargando…</div> : null}
        {!loading && !error && !reports.length ? (
          <div style={{ ...smallMutedStyle, padding: "24px 0", textAlign: "center", fontSize: "14px" }}>
            {filter === "NUEVO" ? "No hay reportes nuevos." : "No hay reportes en esta lista."}
          </div>
        ) : null}

        {reports.map((r) => (
          <div key={r.id} style={rowStyle}>
            <button type="button" style={rowHeadStyle} aria-expanded={openId === r.id} onClick={() => setOpenId(openId === r.id ? null : r.id)}>
              <span style={{ fontSize: "20px", lineHeight: "22px" }} title={KIND_LABEL[r.kind]}>{KIND_ICON[r.kind]}</span>
              <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "baseline" }}>
                  <strong style={{ fontSize: "14px" }}>{quien(r)}</strong>
                  <span style={smallMutedStyle}>{fecha(r.created_at)}</span>
                  {r.has_screenshot ? <span style={smallMutedStyle}>📎 captura</span> : null}
                  {filter === "TODOS" ? <span style={smallMutedStyle}>· {STATUS_LABEL[r.status]}</span> : null}
                </span>
                {openId === r.id ? null : <span style={previewStyle}>{r.message}</span>}
                {openId === r.id || !r.page ? null : <span style={smallMutedStyle}>{r.page}</span>}
              </span>
            </button>
            {openId === r.id ? <ReportDetail report={r} token={token} onSaved={onSaved} /> : null}
          </div>
        ))}
      </Card>
    </div>
  );
}
